import React from 'react';
import PropTypes from 'prop-types';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { Row, Col, Input, Table, Pagination, Drawer } from 'antd';
import isEqual from 'lodash/isEqual';
import throttle from 'lodash/throttle';

import * as botParamsActions from '../../../../../data/redux/botparams_details/actions';
import { pageChanged } from '../../../../../data/redux/page_details/actions';

import { APP_PAGES } from '../../../../../data/config/constants';
import { moveCaretAtEnd, uniqueId, showMessage } from '../../../../../data/config/utils';

import ErrorBoundary from '../../../../../components/errorboundary';
import CustomHeader from '../../../../../components/customheader';
import TableCellAction from '../../../components/tablecellaction';
import AddInputItem from '../../../../../components/form/addinputItem';
import CardContainer from '../../../components/cardcontainer';
import VariableForm from './variableform';

class BotParams extends React.PureComponent {
    constructor(props) {
        super(props);
        this.state = {
            variable_search_text: "",
            filtered_variables: props.botparams_details.variables,
            drawer_data: {
                title: "",
                current_variable: null,
                show_create_variable: false,
                show_edit_variable: false,
            },
            current_action_page: 1,
            editable_action: {},
            new_action: "",
            action_search_text: "",
            filtered_actions: props.botparams_details.actions,
        };
        this.variableTableColumns = this.getVariableTableColumns();
        this.throttleUpdateFilterStateBySearchText = throttle(this.updateFilterStateBySearchText, 500);
    }

    componentDidMount() {
        this.props.actions.pageChanged(APP_PAGES.BOT_PARAMS, APP_PAGES.BOT_BUILDER);
        this.props.actions.getAllVariables();
        this.props.actions.getAllActions();
    }

    componentWillReceiveProps(nextProps) {
        const { botparams_details } = this.props;
        const { action_search_text, variable_search_text } = this.state;

        if (!isEqual(nextProps.botparams_details.variables, botparams_details.variables)) {
            const variables = [...nextProps.botparams_details.variables];
            if (variable_search_text.trim().length !== 0) {
                this.setState({
                    filtered_variables: this.filterBySearchText(variable_search_text, variables)
                });
            } else {
                this.setState({
                    filtered_variables: variables
                });
            }
        }
        if (!isEqual(nextProps.botparams_details.actions, botparams_details.actions)) {
            const actions_data = [...nextProps.botparams_details.actions];
            if (action_search_text.trim().length !== 0) {
                this.setState({
                    filtered_actions: this.filterBySearchText(action_search_text, actions_data)
                });
            } else {
                this.setState({
                    filtered_actions: actions_data
                });
            }
        }
    }

    getVariableTableColumns = () => {
        const columns = [
            {
                title: 'Name',
                dataIndex: 'name',
                key: 'name',
            },
            {
                title: 'Options',
                dataIndex: 'options',
                key: 'options',
                render: options => (
                    <div className="ori-flex-row ori-flex-wrap">
                        {
                            options && options.map((option, index) => {
                                return (
                                    <div key={index} className="ori-lr-pad-10 ori-bg-default ori-border-radius-10 ori-font-xs ori-font-primary ori-mrgn-3">{option}</div>
                                );
                            })
                        }
                    </div>
                )
            },
            {
                title: 'Validator',
                dataIndex: 'validator',
                key: 'validator',
            },
            {
                title: 'Action',
                dataIndex: 'actions',
                key: 'actions',
                align: 'center',
                render: (text, variable) => (<TableCellAction delete_popconfirm_title="Are you sure delete this variable?" handleEditClick={this.handleVariableEditing.bind(this, variable)} handleDeleteClick={this.handleVariableDelete.bind(this, variable)} show_edit show_delete align_center />)
            },
        ];
        return columns;
    };

    onClickAddNewVariable = () => {
        this.setState({
            drawer_data: {
                title: "Create new variable",
                current_variable: {
                    name: "",
                    validator: "",
                    options: []
                },
                show_edit_variable: false,
                show_create_variable: true,
            }
        });
    };

    handleVariableEditing = current_variable => {
        this.setState({
            drawer_data: {
                title: "Edit variable",
                current_variable,
                show_edit_variable: true,
                show_create_variable: false,
            }
        });
    };

    handleVariableDelete = variable => {
        if (variable && variable._id) {
            const payload = { _id: variable._id }
            this.props.actions.deleteVariable(payload);
        } else {
            showMessage('error', '_id is missing in variable.')
        }
    };

    handleCurrentVariableChange = (key, e) => {
        const { drawer_data } = this.state;
        const value = e.target.value;
        this.setState({
            drawer_data: {
                ...drawer_data,
                current_variable: {
                    ...drawer_data.current_variable,
                    [key]: value
                },
            }
        });
    };

    addNewVariableOption = option => {
        const { drawer_data } = this.state;
        if (drawer_data.current_variable.options) {
            const index = drawer_data.current_variable.options.findIndex(opt => opt === option);
            if (index === -1) {
                this.setState({
                    drawer_data: {
                        ...drawer_data,
                        current_variable: {
                            ...drawer_data.current_variable,
                            options: [...drawer_data.current_variable.options, option]
                        },
                    }
                });
            } else {
                showMessage('warning', `${option} is already exist.`)
            }
        } else {
            this.setState({
                drawer_data: {
                    ...drawer_data,
                    current_variable: {
                        ...drawer_data.current_variable,
                        options: [option]
                    },
                }
            });
        }
    };

    removeVariableOption = index => {
        const { drawer_data } = this.state;
        this.setState({
            drawer_data: {
                ...drawer_data,
                current_variable: {
                    ...drawer_data.current_variable,
                    options: [...drawer_data.current_variable.options.slice(0, index), ...drawer_data.current_variable.options.slice(index + 1)]
                },
            }
        });
    };

    handleVariableFormSubmit = () => {
        const { actions } = this.props;
        const { drawer_data } = this.state;
        if (drawer_data.show_create_variable) {
            actions.createVariable(drawer_data.current_variable, this.closeModal);
        } else if (drawer_data.show_edit_variable) {
            actions.updateVariable(drawer_data.current_variable, this.closeModal);
        }
    };

    closeModal = () => {
        this.setState({
            drawer_data: {
                title: "",
                current_variable: null,
                show_edit_variable: false,
                show_create_variable: false,
            }
        });
    };

    filterBySearchText = (text, array) => {
        return array.filter(item => {
            return (item.name && item.name !== undefined && item.name.toLowerCase().indexOf(text.toLowerCase()) !== -1);
        });
    };

    updateFilterStateBySearchText = (text, array, key) => {
        if (text.trim().length !== 0) {
            this.setState({
                [key]: this.filterBySearchText(text, array)
            });
        } else {
            this.setState({
                [key]: array
            });
        }
    };

    handleVariableSearch = event => {
        const variable_search_text = event.target.value;
        const { botparams_details } = this.props;
        this.setState({ variable_search_text });
        this.throttleUpdateFilterStateBySearchText(variable_search_text, botparams_details.variables, 'filtered_variables');
    };

    handleActionSearch = event => {
        const action_search_text = event.target.value;
        const { botparams_details } = this.props;
        this.setState({ action_search_text });
        this.throttleUpdateFilterStateBySearchText(action_search_text, botparams_details.actions, 'filtered_actions');
    };

    onChangeActionPage = current_action_page => {
        this.setState({ current_action_page });
    };

    startActionEditing = editable_action => {
        this.setState({ editable_action });
    };

    stopActionEditing = () => {
        this.setState({ editable_action: {} });
    };

    onChangeAddInputItem = e => {
        this.setState({ new_action: e.target.value });
    };

    addNewAction = () => {
        const { new_action } = this.state;
        if (new_action && new_action.trim().length > 0) {
            const action = { name: new_action.trim() };
            this.props.actions.createAction(action, () => this.setState({ new_action: "" }));
        }
    };

    onKeyDownAddAction = e => {
        if (e.keyCode === 13) {
            this.addNewAction();
        }
    };

    onChangeAction = e => {
        const { editable_action } = this.state;
        this.setState({
            editable_action: {
                ...editable_action,
                name: e.target.value
            }
        });
    };

    onKeyDownEditAction = e => {
        const { editable_action } = this.state;
        if (e.keyCode === 13) {
            this.props.actions.updateAction(editable_action, this.stopActionEditing);
        }
    };

    handleActionDelete = action => {
        if (action._id) {
            const payload = { _id: action._id };
            this.props.actions.deleteAction(payload);
        } else {
            showMessage('error', 'action ID is missing');
        }
    };

    renderVariablesTable = () => {
        const { filtered_variables } = this.state;
        const { screen_width, botparams_details, actions } = this.props;
        const custom_header_props = {
            title: "Variables",
            search_placeholder: "Search variables",
            show_search: true,
            onChangeSearch: this.handleVariableSearch,
            search_disabled: botparams_details.loaders.variables_load_error,
            show_first_button: true,
            first_button_text: "Add New",
            first_button_type: "primary",
            onClickFirstButton: this.onClickAddNewVariable
        };
        return (
            <CardContainer custom_header_props={custom_header_props} container_height={565} emtpy_condition={filtered_variables.length === 0} load_error={botparams_details.loaders.variables_load_error} loading={botparams_details.loaders.variables_loading} err_btn_click={actions.getAllVariables} show_custom_header show_err_btn>
                <Table className="ori-table-default ori-table-header-bg-transparent" size={screen_width <= 576 ? "small" : "middle"} scroll={{ x: 400 }} rowKey={uniqueId} pagination={{ pageSize: 9 }} columns={this.variableTableColumns} dataSource={filtered_variables} />
            </CardContainer>
        );
    };

    renderActionsTable = () => {
        const { editable_action, filtered_actions, current_action_page } = this.state;
        const { botparams_details, actions } = this.props;
        const custom_header_props = {
            title: "Actions",
            search_placeholder: "Search actions",
            show_search: true,
            onChangeSearch: this.handleActionSearch,
            search_disabled: botparams_details.loaders.actions_load_error
        };
        return (
            <CardContainer custom_header_props={custom_header_props} container_height={520} emtpy_condition={filtered_actions.length === 0} load_error={botparams_details.loaders.actions_load_error} loading={botparams_details.loaders.actions_loading} err_btn_click={actions.getAllActions} show_custom_header show_err_btn>
                <div>
                    {
                        filtered_actions.map((action, index) => {
                            const start_page_index = (current_action_page - 1) * 10;
                            const end_page_index = current_action_page * 10;
                            if (start_page_index <= index && index < end_page_index) {
                                return (
                                    <ErrorBoundary key={action._id}>
                                        <div className="ori-flex-row ori-flex-jsb ori-lr-pad-10 ori-tb-pad-7 ori-b-mrgn-5 ori-bg-default ori-border-light">
                                            {
                                                editable_action._id === action._id &&
                                                <Input autoFocus className="ori-height-auto ori-font-default ori-no-pad ori-input-none ori-tb-pad-3" placeholder="edit action" value={editable_action.name} onFocus={moveCaretAtEnd} onBlur={this.stopActionEditing} onKeyDown={this.onKeyDownEditAction} onChange={this.onChangeAction} />
                                            }
                                            {
                                                editable_action._id !== action._id &&
                                                <div className="ori-overflow-hidden ori-flex-column ori-flex-jc ori-tb-pad-3"><span className="ori-text-overflow-dotted">{action.name}</span></div>
                                            }
                                            {
                                                editable_action._id !== action._id &&
                                                <TableCellAction delete_popconfirm_title="Are you sure delete this action?" handleEditClick={this.startActionEditing.bind(this, action)} handleDeleteClick={this.handleActionDelete.bind(this, action)} show_edit show_delete />
                                            }
                                        </div>
                                    </ErrorBoundary>
                                );
                            }
                        })
                    }
                    <div className="ori-tb-pad-10 ori-full-width ori-flex-row ori-flex-jfe">
                        <Pagination size="small" total={filtered_actions.length} onChange={this.onChangeActionPage} />
                    </div>
                </div>
            </CardContainer>
        );
    };

    render() {
        const { botparams_details, screen_width } = this.props;
        const { drawer_data, new_action } = this.state;
        return (
            <Row className="ori-lr-pad-10">
                <CustomHeader title="bot params" bottom_bordered />
                <Row gutter={10}>
                    <Col xs={24} md={16} className="ori-t-pad-5">
                        {this.renderVariablesTable()}
                    </Col>
                    <Col xs={24} md={8} className="ori-t-pad-5">
                        <AddInputItem size="large" placeholder="Add new action" value={new_action} disabled={botparams_details.loaders.actions_loading || botparams_details.loaders.actions_load_error} onChange={this.onChangeAddInputItem} onKeyDown={this.onKeyDownAddAction} onClickBtn={this.addNewAction} />
                        {this.renderActionsTable()}
                    </Col>
                </Row>
                <Drawer className="ori-drawer ori-drawer-title-primary ori-drawer-footer-pad ori-drawer-height-without-footer" width={screen_width < 480 ? screen_width : 460} title={drawer_data.title} visible={drawer_data.show_create_variable || drawer_data.show_edit_variable} placement="right" maskClosable={false} onClose={this.closeModal}>
                    {
                        (drawer_data.show_create_variable || drawer_data.show_edit_variable) &&
                        <ErrorBoundary onErrorCallback={this.closeModal}>
                            <div className="ori-lr-pad-15 ori-full-parent-height ori-overflow-y-auto ori-overflow-x-hidden">
                                <VariableForm loading={botparams_details.loaders.variable_updating || botparams_details.loaders.variable_creating} variable={drawer_data.current_variable} button_ok_text={drawer_data.show_create_variable ? (botparams_details.loaders.variable_creating ? "Creating" : "Create") : (botparams_details.loaders.variable_updating ? "Updating" : "Update")} onChangeName={this.handleCurrentVariableChange.bind(this, 'name')} onChangeValidator={this.handleCurrentVariableChange.bind(this, 'validator')} onClickAddOption={this.addNewVariableOption} onClickDeleteOption={this.removeVariableOption} handleCancelClick={this.closeModal} handleOkClick={this.handleVariableFormSubmit} />
                            </div>
                        </ErrorBoundary>
                    }
                </Drawer>
            </Row>
        );
    }
}

const mapStateToProps = state => {
    return {
        botparams_details: state.botparams_details,
        screen_width: state.page_details.device_data.screen_width,
    };
};

const mapDispatchToProps = dispatch => {
    return {
        actions: bindActionCreators(Object.assign({}, { pageChanged }, botParamsActions), dispatch)
    };
};

BotParams.propTypes = {
    botparams_details: PropTypes.object,
    screen_width: PropTypes.number,
    actions: PropTypes.object,
    history: PropTypes.object,
    match: PropTypes.object,
    isActionPermitted: PropTypes.func,
};

export default connect(mapStateToProps, mapDispatchToProps, null, { withRef: true })(BotParams);



// WEBPACK FOOTER //
// ./src/app/pagecontainer/modules/botbuilder/botparams/index.js